import { Flex, Text } from '@contentful/f36-components';
import { useMemo } from 'react';
import { useBuildDeployUrl } from '../hooks/useBuildDeployUrl';
import { useEntryIsDeployed } from '../hooks/useEntryIsDeployed';
import type { RecentDeployments } from '../types';

interface DeployStatusProps {
  recentDeployments: RecentDeployments;
  environment: 'production' | 'staging';
}

export const DeployStatus = ({ environment, recentDeployments }: DeployStatusProps) => {
  const entryIsDeployed = useEntryIsDeployed(recentDeployments);
  const buildDeployUrl = useBuildDeployUrl(environment);

  const deployStatusProps = useMemo(() => {
    const runDate = new Date(
      recentDeployments.workflow_runs[0].created_at
    ).toLocaleString();
    if (recentDeployments.workflow_runs[0].conclusion === 'success') {
      return {
        fontColor: 'green500',
        children: `Last deploy succeeded - ${runDate}`,
      } as const;
    } else {
      return {
        fontColor: 'red500',
        children: `Last deploy failed - ${runDate}`,
      } as const;
    }
  }, [recentDeployments.workflow_runs]);

  const entryStatusProps = useMemo(() => {
    const url  = buildDeployUrl();
    if (entryIsDeployed) {
      return {
        fontColor: 'green500',
        children:(
        <>
          <a href={url.toString()} target="_blank" rel="noopener noreferrer">
            {url.host}
          </a>
          {' is up-to-date for this entry.'}
        </>
      ),
      } as const;
    } else {
      return {
        fontColor: 'red500',
      children: (
        <>
          <a href={url.toString()} target="_blank" rel="noopener noreferrer">
            {url.host}
          </a>
          {' is out-of-date for this entry.'}
        </>
      ),
      } as const;
    }
  }, [entryIsDeployed, buildDeployUrl]);

  return (
    <Flex flexDirection="column">
      <Text
        fontSize="fontSizeS"
        lineHeight="lineHeightS"
        {...deployStatusProps}
      />
      <Text
        fontSize="fontSizeS"
        lineHeight="lineHeightS"
        {...entryStatusProps}
      />
    </Flex>
  );
};
